import {
  formatParisDateTime,
  RECHARGE_WEBHOOK_OUTCOME,
  type RechargeWebhookEventListItem,
} from '@flux/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import type { JSX } from 'react';
import {
  createDataTableColumnHelper,
  DataTable,
  useDataTableUrlState,
} from '@/components/data-table/data-table.js';
import { StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { fetchWebhookEvents } from './api.js';
import { useDetailSheetParam } from './detail-sheet.js';
import {
  isRechargeTokenMissing,
  RECHARGE_NOT_CONFIGURED_EMPTY_STATE,
  useRechargeSummary,
} from './recharge-status.js';
import { WebhookDetailSheet } from './webhook-detail-sheet.js';
import {
  CLEAR_WEBHOOK_FILTERS,
  hasActiveWebhookFilters,
  readWebhookFilters,
  WebhookFilters,
} from './webhook-filters.js';
import { WebhookFeature, WebhookObject } from './webhook-object.js';

const PAGE_SIZE = 50;
/** Triables côté serveur (`apps/server/src/http/api/recharge/index.ts::SORT_FIELDS`). */
const SORTABLE_COLUMNS = ['receivedAt', 'topic', 'outcome'] as const;
const DEFAULT_SORT = 'receivedAt';

const helper = createDataTableColumnHelper<RechargeWebhookEventListItem>();

const columns = helper.columns([
  helper.accessor('receivedAt', {
    id: 'receivedAt',
    header: 'Reçu le',
    cell: (info) => (
      <span className="whitespace-nowrap tabular-nums">
        {formatParisDateTime(new Date(info.getValue()))}
      </span>
    ),
  }),
  helper.accessor('topic', {
    id: 'topic',
    header: 'Topic',
    cell: (info) => (
      <span className="break-all font-mono text-xs" translate="no">
        {info.getValue()}
      </span>
    ),
  }),
  helper.display({
    id: 'object',
    header: 'Objet',
    cell: ({ row }) => (
      <WebhookObject objectType={row.original.objectType} objectId={row.original.objectId} />
    ),
  }),
  helper.accessor('feature', {
    id: 'feature',
    header: 'Fonction',
    cell: (info) => <WebhookFeature feature={info.getValue()} />,
  }),
  helper.accessor('outcome', {
    id: 'outcome',
    header: 'Résultat',
    cell: (info) => <StatusBadge registry={RECHARGE_WEBHOOK_OUTCOME} code={info.getValue()} />,
  }),
  helper.accessor('reason', {
    id: 'reason',
    header: 'Raison',
    // Tronquée dans le tableau ; le texte complet reste dans `title` et dans le panneau.
    cell: (info) => {
      const reason = info.getValue();
      return reason === null ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <span className="block max-w-xs truncate" title={reason}>
          {reason}
        </span>
      );
    },
  }),
]);

/**
 * Onglet Webhooks de l'écran Abonnements (`06` §8.5) : journal des webhooks
 * Recharge reçus, filtres, tri et page dans l'URL, détail dans un panneau
 * porté par `?id=`.
 */
export function WebhooksTab(): JSX.Element {
  const rawSearch: unknown = useSearch({ strict: false });
  const { page, sortBy, sortDir, setPage, setSort, setFilters } = useDataTableUrlState();
  const { id, openDetail, closeDetail } = useDetailSheetParam();
  const summary = useRechargeSummary();
  const filters = readWebhookFilters(rawSearch);
  const isFiltered = hasActiveWebhookFilters(filters);

  const listParams = {
    page,
    pageSize: PAGE_SIZE,
    sortDir,
    ...(sortBy ? { sortBy } : {}),
    ...(filters.topic.length > 0 ? { topic: filters.topic } : {}),
    ...(filters.outcome.length > 0 ? { outcome: filters.outcome } : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.subscriptions.webhookEvents(listParams),
    queryFn: () => fetchWebhookEvents(listParams),
    placeholderData: keepPreviousData,
  });

  function resetFilters(): void {
    setFilters(CLEAR_WEBHOOK_FILTERS);
  }

  return (
    <>
      <DataTable
        caption="Webhooks Recharge reçus"
        columns={columns}
        data={query.data?.items ?? []}
        getRowId={(row) => row.id}
        total={query.data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        sortBy={sortBy ?? DEFAULT_SORT}
        sortDir={sortDir}
        sortableColumnIds={SORTABLE_COLUMNS}
        onPageChange={setPage}
        onSortChange={setSort}
        onRowClick={(row) => {
          openDetail(row.id);
        }}
        isLoading={query.isPending}
        isError={query.isError}
        errorMessage={
          query.error instanceof ApiError
            ? query.error.message
            : 'Impossible de charger les webhooks.'
        }
        errorDetail={query.error instanceof ApiError ? query.error.detail : undefined}
        onRetry={() => {
          void query.refetch();
        }}
        toolbar={<WebhookFilters value={filters} onChange={setFilters} />}
        emptyState={
          isFiltered
            ? {
                title: 'Aucun résultat pour ces filtres',
                description: 'Élargissez la période ou retirez un filtre.',
                action: (
                  <Button type="button" variant="outline" onClick={resetFilters}>
                    Réinitialiser les filtres
                  </Button>
                ),
              }
            : isRechargeTokenMissing(summary.data)
              ? RECHARGE_NOT_CONFIGURED_EMPTY_STATE
              : {
                  title: 'Aucun webhook reçu',
                  description:
                    'Les événements envoyés par Recharge à Flux apparaîtront ici. Enregistrez les webhooks dans Paramètres → Recharge.',
                  action: (
                    <Button asChild variant="outline">
                      <Link to="/parametres" search={{ tab: 'recharge' }}>
                        Ouvrir les réglages Recharge
                      </Link>
                    </Button>
                  ),
                }
        }
      />
      <WebhookDetailSheet id={id} onClose={closeDetail} />
    </>
  );
}
